import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus } from '@azure/msal-browser';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { filter, map, catchError, switchMap, take, timeout, shareReplay } from 'rxjs/operators';
import { API_SCOPE, LOGIN_SCOPES } from '../config/auth-config';

/** Session-scoped guard so a persistent silent-auth failure can't cause an infinite reload loop. */
const AUTH_REDIRECT_FLAG = 'nest_auth_redirect_attempted';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<{ displayName: string; email: string } | null>(null);
  private loginTriggered = false;

  constructor(
    private msalService: MsalService,
    private msalBroadcast: MsalBroadcastService,
    private http: HttpClient
  ) {
    this.initializeUser();
  }

  /**
   * Emits once MSAL has finished startup/redirect handling (inProgress === None),
   * so callers never race against initialization. shareReplay(1) means late
   * subscribers (e.g. the first API call at bootstrap) immediately get the cached
   * "ready" signal instead of hanging.
   */
  private readonly msalReady$ = this.msalBroadcast.inProgress$.pipe(
    filter((status: InteractionStatus) => status === InteractionStatus.None),
    take(1),
    shareReplay(1)
  );


  private initializeUser() {
    // Handle redirect response on page load (required for loginRedirect flow)
    this.msalService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult | null) => {
        if (result?.account) {
          this.msalService.instance.setActiveAccount(result.account);
          this.updateCurrentUser();
        }
      },
      error: (err) => console.error('[Auth] Redirect handle error:', err)
    });

    // Primary: get user from SWA /.auth/me (works when SWA Easy Auth is configured)
    this.getUserFromSWA();

    // Fallback: wait for MSAL ready then check cached accounts
    this.msalBroadcast.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .subscribe(() => {
        if (!this.currentUserSubject.value) {
          this.updateCurrentUser();
        }
        // If still no user on a deployed environment, try auto-login
        if (!this.currentUserSubject.value && !this.loginTriggered && window.location.hostname !== 'localhost') {
          this.loginTriggered = true;
          this.tryAutoLogin();
        }
      });
  }

  private tryAutoLogin() {
    // Full-page redirect (no iframe) — avoids X-Frame-Options: DENY and CSP frame-src
    // blocks that make ssoSilent fail. Requires the redirect URI to be registered
    // under the SPA platform in the Entra app registration (fixes AADSTS9002326).
    // LOGIN_SCOPES includes the API scope so consent for the access token the
    // Function validates is captured up front (avoids interaction_required later).
    this.msalService.loginRedirect({ scopes: LOGIN_SCOPES });
  }

  private getUserFromSWA() {
    this.http.get<any>('/.auth/me').subscribe({
      next: (authInfo) => {
        if (authInfo?.clientPrincipal) {
          const principal = authInfo.clientPrincipal;
          // Extract first name only
          const nameClaim = principal.claims?.find((c: any) => c.typ === 'name')?.val;
          const givenNameClaim = principal.claims?.find((c: any) => c.typ === 'given_name')?.val;
          let firstName = givenNameClaim; // Prefer given_name claim
          
          // Fallback: parse from full name
          if (!firstName && nameClaim) {
            if (nameClaim.includes(',')) {
              // Format: "Last, First" — extract after comma
              firstName = nameClaim.split(',')[1]?.trim();
            } else if (nameClaim.includes(' ')) {
              // Format: "First Last" — extract before space
              firstName = nameClaim.split(' ')[0]?.trim();
            } else {
              firstName = nameClaim.trim();
            }
          }
          
          const displayName = firstName || principal.userDetails || 'User';
          // Use userDetails as email fallback (SWA sets this to the user's UPN/email)
          const email = principal.claims?.find((c: any) =>
            c.typ === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress' ||
            c.typ === 'preferred_username' ||
            c.typ === 'upn'
          )?.val || principal.userDetails || '';
          this.currentUserSubject.next({ displayName, email });
        } else if (window.location.hostname === 'localhost') {
          this.currentUserSubject.next({ displayName: 'Dev', email: 'dev@chevron.com' });
        }
        // On deployed env with no principal: MSAL broadcast handler will trigger login
      },
      error: (_err) => {
        // Expected in local dev — /.auth/me only exists in Azure Static Web Apps
        if (window.location.hostname === 'localhost') {
          this.currentUserSubject.next({ displayName: 'Dev', email: 'dev@chevron.com' });
        }
        // On deployed env: MSAL broadcast handler will trigger login
      }
    });
  }

  private updateCurrentUser() {
    let account = this.msalService.instance.getActiveAccount();
    const allAccounts = this.msalService.instance.getAllAccounts();
    if (!account) {
      if (allAccounts?.length > 0) {
        account = allAccounts[0];
        this.msalService.instance.setActiveAccount(account);
      }
    }
    if (account) {
      // Extract first name only
      const claims = (account.idTokenClaims as any) || {};
      const givenName = claims.given_name;
      let firstName = givenName;
      
      // Fallback: parse from full name
      if (!firstName) {
        const fullName = account.name || claims.name || account.username || 'User';
        if (fullName.includes(',')) {
          firstName = fullName.split(',')[1]?.trim();
        } else if (fullName.includes(' ')) {
          firstName = fullName.split(' ')[0]?.trim();
        } else {
          firstName = fullName;
        }
      }
      
      const displayName = firstName || 'User';
      const email = claims.preferred_username || account.username || '';
      this.currentUserSubject.next({ displayName, email });
    }
  }

  getCurrentUser$(): Observable<{ displayName: string; email: string } | null> {
    return this.currentUserSubject.asObservable();
  }

  getCurrentUser(): string {
    return this.currentUserSubject.value?.displayName || 'User';
  }

  getCurrentUserEmail(): string {
    return this.currentUserSubject.value?.email || 'unknown';
  }

  /**
   * Silently acquires an access token for the NEST API scope so the Function can
   * cryptographically verify the caller's identity (replaces the spoofable
   * X-User-Email header). Emits null on failure — the request still proceeds and
   * telemetry is attributed to "anonymous" rather than breaking the call.
   *
   * When the silent refresh fails with InteractionRequiredAuthError (token expired
   * and no refresh token available, e.g. after a long idle session), a full-page
   * redirect re-establishes the session. A guard flag prevents redirect loops.
   */
  private tokenRedirectPending = false;

  getApiToken(): Observable<string | null> {
    const redirectUri = `${window.location.origin}/auth-redirect.html`;

    // Wait until MSAL has finished initializing before deciding whether an
    // account exists. Without this gate the very first request at bootstrap
    // (e.g. prewarm in ngOnInit) runs before handleRedirectObservable() has set
    // the active account, gets no token, and 401s. The timeout is a safety net
    // so a stuck MSAL never hangs the request indefinitely.
    return this.msalReady$.pipe(
      timeout({ first: 10000, with: () => of(InteractionStatus.None as InteractionStatus) }),
      switchMap(() => {
        const instance = this.msalService.instance;
        const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];

        if (!account) {
          // MSAL is ready but holds no account (the user authenticated via SWA
          // Easy Auth, which is a separate token cache). Establish an MSAL
          // session silently via a hidden iframe against the exempt redirect page.
          return this.msalService
            .ssoSilent({ scopes: LOGIN_SCOPES, redirectUri })
            .pipe(
              map((result: AuthenticationResult) => {
                if (result.account) instance.setActiveAccount(result.account);
                if (!result.accessToken) console.warn('[Auth] ssoSilent returned no access token');
                if (result.accessToken) sessionStorage.removeItem(AUTH_REDIRECT_FLAG);
                this.logTokenClaims(result.accessToken);
                return result.accessToken || null;
              }),
              catchError((err) => {
                console.warn('[Auth] ssoSilent failed:', err?.errorCode || err?.name || err);
                this.redirectToLoginOnce(err);
                return of(null);
              })
            );
        }

        return this.msalService
          .acquireTokenSilent({ account, scopes: [API_SCOPE], redirectUri })
          .pipe(
            map((result: AuthenticationResult) => {
              if (!result.accessToken) console.warn('[Auth] acquireTokenSilent returned no access token');
              if (result.accessToken) sessionStorage.removeItem(AUTH_REDIRECT_FLAG);
              this.logTokenClaims(result.accessToken);
              return result.accessToken || null;
            }),
            catchError((err) => {
              console.warn('[Auth] acquireTokenSilent failed:', err?.errorCode || err?.name || err);
              this.redirectToLoginOnce(err);
              return of(null);
            })
          );
      })
    );
  }

  /**
   * Triggers a single full-page login redirect when silent auth needs interaction.
   *
   * A full-page loginRedirect is a top-level navigation (no iframe), so it is
   * immune to the X-Frame-Options: DENY / BroadcastChannel-bridge issues that make
   * the MSAL v5 silent iframe time out. It also establishes a fresh refresh token,
   * after which acquireTokenSilent uses the RT network path instead of the iframe.
   *
   * `timed_out` / `redirect_bridge_timeout` are the MSAL v5 silent-iframe timeout
   * codes and MUST be treated as "needs interaction" — otherwise a user whose MSAL
   * cache has no usable refresh token (e.g. authenticated only via SWA Easy Auth)
   * is stuck with no token forever.
   */
  private redirectToLoginOnce(err: any) {
    const code = err?.errorCode;
    const needsInteraction =
      err?.name === 'InteractionRequiredAuthError' ||
      code === 'interaction_required' ||
      code === 'consent_required' ||
      code === 'login_required' ||
      code === 'no_account_error' ||
      code === 'monitor_window_timeout' ||
      code === 'timed_out' ||
      code === 'redirect_bridge_timeout';
    if (!needsInteraction) return;
    // Guard against redirect loops. tokenRedirectPending covers the current page
    // lifetime; the sessionStorage flag survives the full-page reload so a
    // persistent silent failure after login does not cause an infinite reload.
    // The flag is cleared as soon as a token is acquired successfully.
    if (this.tokenRedirectPending || sessionStorage.getItem(AUTH_REDIRECT_FLAG)) return;
    this.tokenRedirectPending = true;
    sessionStorage.setItem(AUTH_REDIRECT_FLAG, '1');
    // LOGIN_SCOPES includes API_SCOPE so consent for both is captured at once.
    this.msalService.loginRedirect({ scopes: LOGIN_SCOPES });
  }

  /**
   * TEMP DIAGNOSTIC: decode (does NOT verify) the JWT payload and log the claims
   * the Function validates. If `aud` is not api://<clientId> or the bare client
   * GUID, or `iss` is not your tenant, the Function will 401 even though MSAL
   * handed us a token. Remove once auth is confirmed working.
   */
  private logTokenClaims(accessToken?: string) {
    if (!accessToken) return;
    try {
      const payload = accessToken.split('.')[1];
      const json = JSON.parse(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      );
      console.info('[Auth] token claims:', {
        aud: json.aud,
        iss: json.iss,
        scp: json.scp,
        appid: json.appid,
        ver: json.ver
      });
    } catch (e) {
      console.warn('[Auth] could not decode token payload:', e);
    }
  }

  login() {
    this.msalService.loginPopup({ scopes: ['user.read'] }).subscribe({
      next: (result: AuthenticationResult) => {
        this.msalService.instance.setActiveAccount(result.account);
        this.updateCurrentUser();
      },
      error: (error) => console.error('[AuthService] Login error:', error)
    });
  }

  logout() {
    const account = this.msalService.instance.getActiveAccount();
    if (account) {
      this.msalService.instance.logoutPopup({
        account,
        postLogoutRedirectUri: window.location.origin
      }).catch((error: any) => console.error('[AuthService] Logout error:', error));
    }
    this.currentUserSubject.next(null);
  }

  isAuthenticated(): boolean {
    return this.msalService.instance.getActiveAccount() !== null;
  }
}
